import React, { MouseEvent } from "react";
// import discountBadge from "../../assets/badge-discount.svg";
import { ActiveCustomSales } from "../../build/activeEntityType";
import { divide, multiply } from "../../utils/math";
import { NodeStates, SimpleCartExpandedNode, StateManager } from "../../build/types";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates
} from "../helpers";
import DynamicIcon from "../DynamicIcon";
import { useInteractionStates } from "../../hooks/useInteractionStates";

const getTotalPrice = (p: ActiveCustomSales) => {
    const totalPrice = multiply(p.price, p.quantity);
    return totalPrice;
};

const SimpleCartCustomSales = ({
    customSale,
    onRemove,
    onSelect,
    dividerBg,
    productStyle,
    productSubtextStyle,
    expandedNode,
    formatCurrency,
    nodeState,
    stateManager,
    builder,
    isCFD,
    disabled
}: {
    customSale: ActiveCustomSales;
    onRemove?: () => void;
    onSelect?: (e: MouseEvent) => void;
    dividerBg?: string;
    productStyle?: React.CSSProperties;
    productSubtextStyle?: React.CSSProperties;
    formatCurrency: (amount: number) => string;
    stateManager?: StateManager;
    disabled?: boolean;
    builder?: boolean;
    nodeState?: NodeStates;
    isCFD?: boolean;
    expandedNode?: SimpleCartExpandedNode;
}) => {
    let DeleteIcon = (
        <svg style={{ width: "32px", height: "32px" }} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" stroke="#ABA7AF" />
            <path d="M5 5L13 13M5 13L13 5" stroke="#ABA7AF" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const { states: buttonStates, setElement: setButtonElement } = useInteractionStates<HTMLSpanElement>();

    const { name, quantity } = customSale;
    const { showItemUnit, showItemUnitLabel, deleteItemIcon, showDeleteIcon } =
        expandedNode as SimpleCartExpandedNode;

    if (deleteItemIcon?.src) {
        const style = getCompoundStyle({
            otherStyle: deleteItemIcon?.style,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_deleteItemIcon_style")
        });
        const colors =
            getStateColorsForBuilder(stateManager, nodeState, "expandedNode_deleteItemIcon_colors") || deleteItemIcon?.colors;
        DeleteIcon = <DynamicIcon name={deleteItemIcon.src} style={style} colors={colors} />;
    }

    if (builder) {
        return (
            <article style={dividerBg ? { borderColor: dividerBg } : {}}>
                <div className="product-row">
                    <div style={{ marginBottom: "8px" }}>
                        <div className="product-main-row">
                            <div className="product-main-row">
                                <span className="product-qty" style={productStyle}>
                                    {quantity} x{" "}
                                    <span className="prod-name" style={productStyle}>
                                        {name}
                                    </span>
                                </span>
                                <span className="product-price" style={productStyle}>
                                    {formatCurrency(getTotalPrice(customSale))}
                                </span>
                            </div>

                            {!isCFD && showDeleteIcon && (
                                <div className="delete-action" data-testid={"builder-remove-product-from-cart-button"}>
                                    <span
                                        className="btn"
                                        onClick={() => !disabled && onRemove && onRemove()}
                                        ref={setButtonElement}
                                    >
                                        {DeleteIcon}
                                    </span>
                                </div>
                            )}
                        </div>

                        {quantity > 1 && (
                            <div className="product-extra-row">
                                <span className="grey unit-price" style={productSubtextStyle}>
                                    {formatCurrency(divide(getTotalPrice(customSale), quantity))}{" "}
                                    {showItemUnit && showItemUnitLabel}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {showDiscountAndFees && (
                            <>
                                {discount && (
                                    <div className="product-extra-row">
                                        <span className="product-extra" style={productSubtextStyle}>
                                            {!productSubtextStyle && <img src={discountBadge} />}
                                            {discount?.label && discount.label}
                                            {discount.isPercent && ` (${multiply(discount.value, 100)}%)`}
                                        </span>
                                        <div className="discount-value-container">
                                            <div className="grey" style={productSubtextStyle}>
                                                -
                                                {formatCurrency(
                                                    discount.isPercent
                                                        ? multiply(discount.value, price * quantity)
                                                        : discount.value
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {fee && (
                                    <div className="product-extra-row indent">
                                        <span className="product-extra" style={productSubtextStyle}>
                                            {!productSubtextStyle && <img src={discountBadge} />}
                                            {fee.label && fee.label}
                                            {fee.isPercent && ` (${multiply(fee.amount, 100)}%)`}
                                        </span>
                                        <div className="fee-value-container">
                                            <div className="grey" style={productSubtextStyle}>
                                                +
                                                {formatCurrency(
                                                    fee.isPercent ? multiply(fee.amount, price * quantity) : fee.amount
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div> */}
                </div>
            </article>
        );
    } else {
        if (deleteItemIcon?.src) {
            const style = getStylesFromInteractiveStates({
                states: buttonStates,
                defaultStyle: deleteItemIcon.style,
                stateManager,
                paths: ["expandedNode_deleteItemIcon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const colors = getColorsFromInteractiveStates({
                states: buttonStates,
                stateManager,
                paths: ["expandedNode_deleteItemIcon_colors"],
                defaultColors: deleteItemIcon.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            DeleteIcon = <DynamicIcon name={deleteItemIcon.src} style={{ width: "18px", ...style }} colors={colors} />;
        }

        return (
            <article style={dividerBg ? { borderColor: dividerBg } : {}}>
                <div
                    className="product-row"
                    onClick={e => {
                        e.stopPropagation();
                        onSelect && onSelect(e);
                    }}
                >
                    <div style={{ marginBottom: "8px" }}>
                        <div className="product-main-row">
                            <div className="product-main-row">
                                <span className="product-qty" style={productStyle}>
                                    {quantity} x{" "}
                                    <span className="prod-name" style={productStyle}>
                                        {name}
                                    </span>
                                </span>
                                <span className="product-price" style={productStyle}>
                                    {formatCurrency(getTotalPrice(customSale))}
                                </span>
                            </div>

                            {!isCFD && showDeleteIcon && (
                                <div className="delete-action" data-testid={"remove-product-from-cart-button"}>
                                    <span
                                        className="btn"
                                        onClick={e => {
                                            e.stopPropagation();
                                            !disabled && onRemove && onRemove();
                                        }}
                                        ref={setButtonElement}
                                    >
                                        {DeleteIcon}
                                    </span>
                                </div>
                            )}
                        </div>

                        {quantity > 1 && (
                            <div className="product-extra-row">
                                <span className="grey unit-price" style={productSubtextStyle}>
                                    {formatCurrency(divide(getTotalPrice(customSale), quantity))}{" "}
                                    {showItemUnit && showItemUnitLabel}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {showDiscountAndFees && (
                            <>
                                {discount && (
                                    <div className="product-extra-row">
                                        <span className="product-extra" style={productSubtextStyle}>
                                            {!productSubtextStyle && <img src={discountBadge} />}
                                            {discount?.label && discount.label}
                                            {discount.isPercent && ` (${multiply(discount.value, 100)}%)`}
                                        </span>
                                        <div className="discount-value-container">
                                            <div className="grey" style={productSubtextStyle}>
                                                -
                                                {formatCurrency(
                                                    discount.isPercent
                                                        ? multiply(discount.value, price * quantity)
                                                        : discount.value
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {fee && (
                                    <div className="product-extra-row indent">
                                        <span className="product-extra" style={productSubtextStyle}>
                                            {!productSubtextStyle && <img src={discountBadge} />}
                                            {fee.label && fee.label}
                                            {fee.isPercent && ` (${multiply(fee.amount, 100)}%)`}
                                        </span>
                                        <div className="fee-value-container">
                                            <div className="grey" style={productSubtextStyle}>
                                                +
                                                {formatCurrency(
                                                    fee.isPercent ? multiply(fee.amount, price * quantity) : fee.amount
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div> */}
                </div>
            </article>
        );
    }
};

export default SimpleCartCustomSales;
